import { identifyInventoryType, inventoryTypes } from './inventoryTypes';
import { identifyQuoteType, quoteTypes } from './quoteTypes';

export function getQuoteTotal(quote) {
  let total = quote.reduce((total, item) => total + calculateItemPrice(item), 0);

  return total;
}

export function getQuoteLeaseTotal(quote) {
  let total = quote.reduce((total, item) => {
    return total + calculateItemLeasePrice(item);
  }, 0);

  return total;
}

function calculateItemPrice(item) {
  switch (identifyQuoteType(item)) {
    case quoteTypes.bundle:
      return calculateBundlePrice(item);
    case quoteTypes.product:
      return calculatePrice(item);
    default:
      throw new Error('unknown inventory type');
  }
}

function calculateItemLeasePrice(item) {
  switch (identifyQuoteType(item)) {
    case quoteTypes.bundle:
      return calculateBundleLeasePrice(item);
    case quoteTypes.product:
      return calculateLeasePrice(item);
    default:
      throw new Error('unknown inventory type');
  }
}

function calculateBundlePrice({ bundle, quantityTree }) {
  const { price } = bundle;
  let { main, included } = quantityTree;
  included = included ? included : [];
  let extraProducts = [main, ...included]
    .map(({ product, quantity, bundledProductQuantifier: { quantity: includedQty } }) => {
      let extraQty = quantity - includedQty;
      return {
        product,
        quantity: extraQty > 0 ? extraQty : 0,
      };
    })
    .map(calculatePrice)
    .reduce((total, price) => total + price);

  return price + extraProducts;
}

function calculateBundleLeasePrice({ bundle, quantityTree }) {
  const { leasingPrice } = bundle;
  let { main, included } = quantityTree;
  included = included ? included : [];
  let extraProducts = [main, ...included]
    .map(({ product, quantity, bundledProductQuantifier: { quantity: includedQty } }) => {
      let extraQty = quantity - includedQty;
      return {
        product,
        quantity: extraQty > 0 ? extraQty : 0,
      };
    })
    .map(calculateLeasePrice)
    .reduce((total, leasingPrice) => {
      if (process.env.NODE_ENV === 'development' && leasingPrice <= 0)
        console.warn('This will be an invalid total sum! - we need a monthly sum for all the bundles calculated');
      return total + leasingPrice;
    });

  return leasingPrice + extraProducts;
}

function calculatePrice({ product, quantity }) {
  const { price = -1 } = product || {};

  if (price < 0) return 0;
  return price * quantity;
}

function calculateLeasePrice({ product, quantity }) {
  const { leasingPrice = -1 } = product || {};
  if (process.env.NODE_ENV === 'development' && !leasingPrice)
    console.warn(`${product.heading} is missing a monthly price`);

  if (leasingPrice < 0) return 0;
  return leasingPrice * quantity;
}

export function getItemInQuote(quote, item) {
  let type = identifyInventoryType(item); // ! needs to be changed - simplified. Just fetch the item, but the elem needs to be considered on arrival at destination
  switch (type) {
    case quoteTypes.product: {
      return getProduct(quote, item);
    }
    case quoteTypes.bundle: {
      return getBundle(quote, item);
    }
    default:
      throw new Error(`Cannot get item of type ${type} from quote`);
  }
}

function getProduct(quote, item) {
  return quote
    .filter(item => identifyQuoteType(item) === inventoryTypes.product)
    .find(({ product }) => {
      if (product?.selectedMount?.id !== item?.selectedMount?.id) return false;
      return product.id === item.id;
    });
}

function getBundle(quote, bundle) {
  return quote
    .filter(item => identifyQuoteType(item) === inventoryTypes.bundle)
    .find(item => item.bundle.id === bundle.id);
}

export function generateQuoteItem(item) {
  switch (identifyInventoryType(item)) {
    case inventoryTypes.bundle: {
      const mainProduct = getQuantifierData(item);

      const quantityTree = {
        main: {
          ...mainProduct,
          bundledProductQuantifier: mainProduct,
        },
        included: item.includedProducts?.map(includedProduct => {
          const { product, quantity } = includedProduct;
          return {
            product,
            quantity,
            bundledProductQuantifier: includedProduct,
          };
        }),
      };
      return { bundle: item, quantityTree };
    }
    case inventoryTypes.product:
      return { product: item, quantity: 1 };
    default:
      throw new Error('unknown inventory type');
  }
}

/**
 * extract data from either a _product bundle quantifier_ or a _product_
 */
function getQuantifierData(item) {
  if ('product' in item.product && 'quantity' in item.product) {
    return item.product;
  }

  const { product, quantity = 1 } = item;
  return { product, quantity };
}

export * from './quoteTypes';
export * from './inventoryTypes';
